import { lazy, Suspense, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";

import { EmptyState, LoadingState } from "../states/index.js";
import { isEmptyBars, type BarBucket, type BarSeries } from "./bars.js";

/**
 * A stacked bar chart over discrete buckets.
 *
 * The boundary that keeps Recharts out of the main bundle — `lazy` at MODULE SCOPE, not inside the
 * component, because calling it per render creates a new lazy component every time and remounts the
 * chart on each parent render.
 */
const Impl = lazy(() =>
  import("./StackedBarChartImpl.js").then((module) => ({ default: module.StackedBarChartImpl })),
);

export interface StackedBarChartProps {
  series: BarSeries[];
  buckets: BarBucket[];
  emptyMessage?: ReactNode;
}

export function StackedBarChart({
  series,
  buckets,
  emptyMessage,
}: StackedBarChartProps): ReactNode {
  const t = useT();
  // Checked BEFORE the boundary, so an empty chart never downloads the library. A household with no
  // income yet is the common case on a fresh install and should not pay for a chart it cannot draw.
  if (isEmptyBars(series, buckets))
    return <EmptyState>{emptyMessage ?? t("ui.chart.empty")}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl series={series} buckets={buckets} />
    </Suspense>
  );
}
